/**
 * ラボの盤面と記録（components.md §3.2 の S: state / log / history / moved）。
 * 段階のタブやラボを切り替えても記録が残るよう、ラボごとに 1 つをモジュールで持つ。
 * ロジックは src/lib/labs を呼ぶだけ。
 */
import {
  applyOp,
  currentState,
  dictModel,
  emptyQueue,
  EMPTY_DICT,
  EMPTY_STACK,
  INITIAL_SHELVES,
  queueModel,
  recordCode,
  replayPlan,
  shelvesModel,
  stackModel,
  startRun,
  undoOp,
  type ApplyResult,
  type DictOp,
  type DictState,
  type LabId,
  type LabModel,
  type LabRun,
  type QueueOp,
  type QueueState,
  type ReplayPlan,
  type ShelvesOp,
  type ShelvesState,
  type StackOp,
  type StackState,
} from '../../lib/labs';

export class LabSession<S, O> {
  readonly model: LabModel<S, O>;
  run: LabRun<S>;
  /** 直前の操作の結果（盤面の下の 1 文と、動いた箱の数）。 */
  last: ApplyResult<S> | null = $state.raw(null);
  /** 段階 3 のエディタの中身。null なら記録から作る。 */
  editorCode: string | null = $state(null);
  /** 操作の通し番号（アニメーションのきっかけに使う）。 */
  tick = $state(0);

  constructor(model: LabModel<S, O>, initial: S) {
    this.model = model;
    this.run = $state.raw(startRun(initial));
  }

  get state(): S {
    return currentState(this.run);
  }

  get entries() {
    return this.run.steps.map((s) => s.entry);
  }

  /** 記録欄をコピーするときの本文。 */
  get code(): string {
    return recordCode(this.model, this.run);
  }

  /** 再現実行のコードと期待フレーム。 */
  get plan(): ReplayPlan {
    return replayPlan(this.model, this.run);
  }

  apply(op: O): ApplyResult<S> {
    const { run, result } = applyOp(this.model, this.run, op);
    this.run = run;
    this.last = result;
    this.tick += 1;
    return result;
  }

  undo(): boolean {
    if (this.run.steps.length === 0) return false;
    this.run = undoOp(this.run);
    this.last = null;
    this.tick += 1;
    return true;
  }

  reset(initial: S = this.run.initial): void {
    this.run = startRun(initial);
    this.last = null;
    this.tick += 1;
  }

  /** 記録を段階 3 のエディタへ送る。 */
  sendToEditor(): void {
    this.editorCode = this.plan.code;
  }
}

export const sessions = {
  stack: new LabSession<StackState, StackOp>(stackModel, EMPTY_STACK),
  queue: new LabSession<QueueState, QueueOp>(queueModel, emptyQueue('head')),
  dict: new LabSession<DictState, DictOp>(dictModel, EMPTY_DICT),
  shelves: new LabSession<ShelvesState, ShelvesOp>(shelvesModel, INITIAL_SHELVES),
};

const INTRO_KEY = 'ic:lab-intro';

function readIntro(): Record<LabId, boolean> {
  const none = { stack: false, queue: false, dict: false };
  try {
    const raw = globalThis.sessionStorage?.getItem(INTRO_KEY);
    if (!raw) return none;
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return none;
    const r = v as Record<string, unknown>;
    return { stack: r['stack'] === true, queue: r['queue'] === true, dict: r['dict'] === true };
  } catch {
    return none;
  }
}

/**
 * 手順 0「困る場面」を通ったか（labs.md）。通るまで盤面を出さず、まず素朴な方法で解かせる。
 * タブを閉じるまで覚える（sessionStorage）。
 */
export const intro: Record<LabId, boolean> = $state(readIntro());

export function finishIntro(id: LabId): void {
  intro[id] = true;
  try {
    globalThis.sessionStorage?.setItem(INTRO_KEY, JSON.stringify(intro));
  } catch {
    // 保存できなくても、このページの間は覚えている
  }
}

/** 動きを減らす設定か（OS の設定、または設定画面の `html[data-motion="reduce"]`）。 */
export function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined' && document.documentElement.dataset['motion'] === 'reduce') {
    return true;
  }
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** 箱の移動の時間（tokens.css の --dur-slow）。動きを減らす設定なら 0。 */
export function slowMs(): number {
  return prefersReducedMotion() ? 0 : 400;
}
